import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Check, X, Layers, ListChecks } from "lucide-react";

export function GroupsBar({ workshopId, groups, setGroups, active, setActive, counts }) {
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const call = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const add = () => call(async () => {
    const { data } = await api.post(`/business/workshops/${workshopId}/groups`, {});
    setGroups([...groups, data]); setActive(data.id);
  }, "Grupo creado");
  const rename = () => call(async () => {
    const { data } = await api.patch(`/business/workshops/${workshopId}/groups/${editing}`, { name });
    setGroups(groups.map((g) => (g.id === data.id ? { ...g, name: data.name } : g))); setEditing(null);
  }, "Nombre actualizado");
  const remove = (g) => call(async () => {
    await api.delete(`/business/workshops/${workshopId}/groups/${g.id}`);
    const rest = groups.filter((x) => x.id !== g.id);
    setGroups(rest); setActive(rest[0]?.id);
  }, "Grupo eliminado");
  const cur = groups.find((g) => g.id === active);
  return (
    <div className="rounded-xl border border-border p-3 space-y-2" data-testid="groups-bar">
      <div className="flex items-center justify-between">
        <Label className="flex items-center gap-1.5"><Layers className="w-4 h-4 text-primary" />Grupos ({groups.length}/10)</Label>
        {groups.length < 10 && <Button size="sm" variant="outline" onClick={add} data-testid="add-group-btn"><Plus className="w-4 h-4 mr-1" />Agregar grupo</Button>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {groups.map((g) => (
          <button key={g.id} type="button" onClick={() => { setActive(g.id); setEditing(null); }} data-testid={`maestro-group-tab-${g.id}`}
            className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors ${g.id === active ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
            {g.name} <span className="opacity-70">({counts[g.id] || 0})</span>
          </button>
        ))}
      </div>
      {cur && (editing === cur.id ? (
        <div className="flex items-center gap-1.5">
          <Input value={name} maxLength={30} onChange={(e) => setName(e.target.value)} className="h-8" data-testid="group-name-input" />
          <Button size="icon" className="h-8 w-8" onClick={rename} data-testid="group-name-save"><Check className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
        </div>
      ) : (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span className="flex-1">Las sesiones que agregues quedan en <strong className="text-foreground">{cur.name}</strong>.</span>
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => { setEditing(cur.id); setName(cur.name); }} data-testid="group-rename-btn"><Pencil className="w-3.5 h-3.5 mr-1" />Renombrar</Button>
          {groups.length > 1 && <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive" onClick={() => remove(cur)} data-testid="group-delete-btn"><Trash2 className="w-3.5 h-3.5" /></Button>}
        </div>
      ))}
    </div>
  );
}

export function EnrollmentModeField({ f, setF }) {
  const opt = (id, title, text, Icon) => (
    <button type="button" onClick={() => setF({ ...f, enrollment_mode: id, max_sessions: f.max_sessions || (id === "free" ? 4 : null) })}
      data-testid={`enroll-mode-${id}`}
      className={`flex-1 text-left rounded-xl border-2 p-3 transition-colors ${f.enrollment_mode === id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
      <span className="flex items-center gap-1.5 text-sm font-semibold"><Icon className="w-4 h-4 text-primary" />{title}</span>
      <span className="block text-xs text-muted-foreground mt-1">{text}</span>
    </button>
  );
  return (
    <div className="space-y-2" data-testid="enroll-mode-field">
      <Label>¿Cómo van a elegir los alumnos sus sesiones?</Label>
      <div className="flex flex-col sm:flex-row gap-2">
        {opt("group", "Por grupo", "El alumno escoge un grupo y queda inscrito a todas las sesiones.", Layers)}
        {opt("free", "Libre selección", "El alumno elige las sesiones de cualquier grupo, hasta llegar al máximo.", ListChecks)}
      </div>
      {f.enrollment_mode === "free" && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Máximo de sesiones por alumno</span>
          <Input type="number" min="1" max="50" className="w-20 h-8" value={f.max_sessions || ""} data-testid="ws-max-sessions"
            onChange={(e) => setF({ ...f, max_sessions: Math.min(50, Number(e.target.value) || 0) || null })} />
        </div>
      )}
    </div>
  );
}
